import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrency } from "@/utils";
import { useHomeMetrics } from "../hooks/use-home-metrics";
import { useMarquee } from "../hooks/use-marquee";

const MetricBox = ({
  label,
  value,
  tooltip,
  ariaHidden,
}: {
  label: string
  value: number | undefined
  tooltip: string
  ariaHidden?: boolean
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <div
        aria-hidden={ariaHidden || undefined}
        className="flex flex-col flex-shrink-0 gap-1 border h-[74px] w-44 lg:h-auto lg:w-auto justify-center items-center  lg:border-none rounded-lg"
      >
        <span className="whitespace-nowrap dark:text-legend">{label}</span>
        {value !== undefined ? (
          <span className="flex items-center font-semibold text-primary dark:text-foreground">
            <span className="opacity-70">$</span>
            {formatCurrency(value, 0, {
              notation: 'compact',
              compactDisplay: 'short',
            })}
          </span>
        ) : (
          <Skeleton className="h-6 w-[50px] bg-primary opacity-70 dark:bg-primary-foreground inline-block" />
        )}
      </div>
    </TooltipTrigger>
    <TooltipContent className="max-w-72 text-center">{tooltip}</TooltipContent>
  </Tooltip>
)

const ProtocolMetrics = () => {
  const { data } = useHomeMetrics()
  const metrics: {
    label: string
    value: number | undefined
    tooltip: string
  }[] = [
      {
        label: 'TVL',
        value: data?.tvl,
        tooltip: 'Sum of all assets held in Reserve smart contracts.',
      },
      {
        label: 'Est. Partner Revenue',
        value: data?.partnerRevenue,
        tooltip:
          'Annualized revenue distributed to DTF creators, index providers, and all other ecosystem partners. Calculated based on TVL and minting fees of all Index DTFs in the last 30 days.',
      },
      {
        label: 'Est. RSR Revenue',
        value: data?.rsrAccrual,
        tooltip:
          'Annualized revenue distributed to RSR stakers and vote-lockers on DTFs or used to burn RSR supply. Calculated based on the last 30 days.',
      },
      {
        label: 'Est. Yield Distribution',
        value: data?.yieldDistributed,
        tooltip:
          'Annualized yield generated by DTFs on Reserve. Calculated based on the last 30 days.',
      },
      {
        label: 'Mint Volume',
        value: data?.mintVolume,
        tooltip: 'Sum of all mints of Index and Yield DTFs since inception.',
      },
    ]
  const { ref, disabled } = useMarquee<HTMLDivElement>(metrics.length)

  const renderBoxes = (copy: number) =>
    metrics.map((m) => (
      <MetricBox
        key={`${copy}-${m.label}`}
        label={m.label}
        value={m.value}
        tooltip={m.tooltip}
        ariaHidden={copy > 0}
      />
    ))

  return (
    <div className="w-full min-w-0 lg:w-auto overflow-hidden lg:overflow-visible pl-2 lg:pl-0">
      <div
        ref={ref}
        className="flex items-center gap-2 lg:gap-10 will-change-transform touch-pan-y lg:touch-auto select-none"
      >
        {renderBoxes(0)}
        {!disabled && renderBoxes(1)}
        {!disabled && renderBoxes(2)}
      </div>
    </div>
  )
}

export default ProtocolMetrics
